import React, { useEffect } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationGuardProps {
  children: React.ReactNode;
}

const NavigationGuard: React.FC<NavigationGuardProps> = ({ children }) => {
  const { preventNavigation, currentTask } = (useLoading() as any);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (preventNavigation) {
      // Prevent browser back/forward
      const handlePopState = (e: PopStateEvent) => {
        e.preventDefault();
        // Stay on current page
        window.history.pushState(null, '', location.pathname);
      };

      // Prevent page refresh
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      };

      // Prevent programmatic navigation
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function(...args) {
        if (preventNavigation) {
          console.warn('Navigation blocked during loading:', currentTask);
          return;
        }
        return originalPushState.apply(this, args);
      };

      window.history.replaceState = function(...args) {
        if (preventNavigation) {
          console.warn('Navigation blocked during loading:', currentTask);
          return;
        }
        return originalReplaceState.apply(this, args);
      };

      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
      };
    }
  }, [preventNavigation, currentTask, location.pathname]);

  // Show loading overlay when navigation is prevented
  if (preventNavigation) {
    return (
      <>
        {children}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 text-center">
            <div className="w-8 h-8 mx-auto mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-pink-400 border-r-purple-400"></div>
            </div>
            <h3 className="text-white font-semibold mb-2">Please Wait</h3>
            <p className="text-gray-300 text-sm">
              {currentTask || 'Loading...'}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              Navigation is temporarily disabled
            </p>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};

export default NavigationGuard; 